import * as React from 'react';
import { useIntl } from 'react-intl';
import { useRoute } from 'wouter';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { CmdAlert } from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import localforage from 'localforage';
import { useQueries } from '@tanstack/react-query';
import { IWidgetGraph } from '../../OnlinePanel/OnlinePanelState';
import { IVehicle, IVehicleData } from '../../Vehicles/types';
import SelectRidesModal from '../SelectRidesModal';
import { URL_THROTTLE, URL_GRAPHS } from '../../../router/constants';
import {
  getData,
  getVehicleName,
  getIsCompressed,
  getDataByRideId,
  IGraphResponseData,
  getDateFrom,
  getDateTo,
  getPartData,
  getPartDataByRideId,
  getRideStartDateAndAddress,
  getRideEndDateAndAddress,
} from '../GraphService';
import Driving from './Driving';
import Stopped from './Stopped';
import RideStartAndEnd from '../RideStartAndEnd';
import PartSelecting from '../PartSelecting';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import PositionDetails from '../PositionsDetails';
import {
  isGraphOpenAtom,
  lastClickedTimestampAtom,
  throttleWidgetGraphAtom,
  widgetGraphSelectedValuesAtom,
} from '../GraphState';
import { setLoaderAtom } from '../../../store/recoil/AppState';
import { vehicleDataByIdSelector } from '../../../store/recoil/vehiclesData';
import { vehicleByIdSelector } from '../../../store/recoil/vehicles';
import useAnalytics from '../../../hooks/useAnalytics';

interface IThrottleGraphProps {
  vehicleId?: number;
}

export default function FuelLevelPercOnline(props: IThrottleGraphProps) {
  const { trackEvent } = useAnalytics();
  const [, params] = useRoute(`${URL_GRAPHS}${URL_THROTTLE}/:vehicleId`);
  const { formatMessage: f } = useIntl();

  const [origStatus, setOrigStatus] = React.useState('');
  const setLastClickedTimestamp = useSetRecoilState<string>(lastClickedTimestampAtom);
  const [defaultDates, setDefaultDates] = React.useState<Date[] | undefined>(undefined);
  const vehicleData = useRecoilValue<IVehicleData | null>(
    vehicleDataByIdSelector(props.vehicleId || Number(params?.vehicleId) || 0)
  );
  const setWidgetGraphSelectedValues = useSetRecoilState<string[] | null>(
    widgetGraphSelectedValuesAtom
  );
  const [startDateAndAddress, setStartDateAndAddress] = React.useState('');
  const [lastDateAndAddress, setLastDateAndAddress] = React.useState('');
  const [endRideDateAndAddress, setEndDateAndAddress] = React.useState('');
  const [showSelectRidesModal, setShowSelectRidesModal] = React.useState(false);
  const [rideId, setRideId] = React.useState('');
  const [responseData, setResponseData] = React.useState<IGraphResponseData[]>([]);
  const setLoader = useSetRecoilState<boolean>(setLoaderAtom);
  const [throttleWidgetGraph, setTrhrottleWidgetGraph] = useRecoilState<IWidgetGraph | null>(
    throttleWidgetGraphAtom
  );
  const setIsGraphOpen = useSetRecoilState<boolean | number>(isGraphOpenAtom);
  const [isOrigCompressed, setIsOrigCompressed] = React.useState(false);
  const vehicle = useRecoilValue<IVehicle | null>(
    vehicleByIdSelector(
      props.vehicleId || (params && params.vehicleId ? Number(params.vehicleId) : false)
    )
  );
  const { setDocumentTitle } = useDocumentTitle();

  const resetGraphStates = () => {
    setResponseData([]);
    setStartDateAndAddress('');
    setLastDateAndAddress('');
    setEndDateAndAddress('');
    setIsOrigCompressed(false);
    setOrigStatus(rideId ? 'STOP' : '');
    setLastClickedTimestamp('');
  };

  React.useEffect(() => {
    if (vehicle && params?.vehicleId) {
      const documentTitle = `${f({ id: 'enum.onlineMap.widget.types.throttle' })} ${getVehicleName(
        vehicle
      )}`;
      setDocumentTitle(documentTitle);
    }
  }, [f, params, setDocumentTitle, vehicle]);

  React.useEffect(() => {
    if (vehicle) {
      setIsGraphOpen(vehicle.id);
    }
  }, [setIsGraphOpen, vehicle]);

  const handleDataByRideId = async (id: string): Promise<void> => {
    if (vehicle) {
      setLoader(true);
      const response = await getDataByRideId('throttle', String(vehicle.id), id);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        setRideId(id);
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
        setIsOrigCompressed(getIsCompressed(response));
        localforage.setItem('widgetGraph', {
          vehicleId: vehicle.id,
          data: response,
          name: 'throttle',
          rideId: id,
        });
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const handleData = async () => {
    if (vehicle) {
      setLoader(true);
      const response = await getData('throttle', String(vehicle.id));
      if (response) {
        setResponseData(response);
        if (params) {
          setLastClickedTimestamp(response[response.length - 1]?.timestamp);
        }
        setStartDateAndAddress(getRideStartDateAndAddress(response));
        setLastDateAndAddress(getRideEndDateAndAddress(response));
        setIsOrigCompressed(getIsCompressed(response));
        if (throttleWidgetGraph) {
          setTrhrottleWidgetGraph((oldState: any) => {
            return { ...oldState, timestamp: response[0]?.timestamp };
          });
        }
        if (vehicleData?.status === 'STOP') {
          localforage.setItem('widgetGraph', {
            vehilceId: vehicle.id,
            data: response,
            name: 'throttle',
            rideId: null,
          });
        } else {
          localforage.setItem('widgetGraph', null);
        }
      } else if (!params && throttleWidgetGraph) {
        setTrhrottleWidgetGraph((oldState: any) => {
          return { ...oldState, timestamp: '' };
        });
      } else {
        resetGraphStates();
      }
      setLoader(false);
    }
  };

  const getDataByRideIdFromLocalforage = async (id: string): Promise<void> => {
    const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
    if (
      data &&
      data.data &&
      vehicle &&
      data.vehicleId === vehicle.id &&
      data.name === 'throttle' &&
      data.rideId === id
    ) {
      setResponseData(data.data);

      setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
    } else {
      handleDataByRideId(id);
    }
  };

  const getDataFromLocalforage = async (): Promise<void> => {
    const data: IWidgetGraph | null = await localforage.getItem('widgetGraph');
    if (
      data &&
      data.data &&
      vehicle &&
      data.vehicleId === vehicle.id &&
      data.name === 'throttle' &&
      !data.rideId
    ) {
      setResponseData(data.data);
      if (params) {
        setLastClickedTimestamp(data.data[data.data.length - 1]?.timestamp);
      }
      setStartDateAndAddress(getRideStartDateAndAddress(data.data));
      setLastDateAndAddress(getRideEndDateAndAddress(data.data));
      setIsOrigCompressed(getIsCompressed(data.data));
    } else {
      handleData();
    }
  };

  useQueries({
    queries: [
      {
        queryKey: ['throttleData', vehicle?.id],
        queryFn: () => getDataFromLocalforage(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!(params || (!params && throttleWidgetGraph && !throttleWidgetGraph.timestamp)),
      },
    ],
  });

  const handleModal = () => {
    trackEvent([
      'Throttle graph page',
      'click',
      showSelectRidesModal ? 'Close select ride modal' : 'Open select ride modal',
    ]);
    setShowSelectRidesModal(!showSelectRidesModal);
  };

  const handleRide = (id: string, dates: Date[]) => {
    trackEvent(['Throttle graph page', 'click', 'Select ride']);
    setWidgetGraphSelectedValues(null);
    handleModal();
    handleDataByRideId(id);
    setDefaultDates(dates);
  };

  const handleEndRideDateAndAddress = (address: string) => {
    setEndDateAndAddress(address);
  };

  const handleDataFromNewRide = () => {
    setOrigStatus('DRIVING');
    setRideId('');
    handleData();
  };

  const handleDataAfterFinishedRide = () => {
    setOrigStatus('STOP');
    handleData();
  };

  const renderGraph = () => {
    if (!origStatus) {
      return <div />;
    }
    const isCompressed = getIsCompressed(responseData);
    if (vehicle) {
      return origStatus === 'STOP' || rideId ? (
        <Stopped
          responseData={responseData}
          isCompressed={isCompressed}
          vehicleId={vehicle.id}
          loadData={handleDataFromNewRide}
          isSelectable={isCompressed && !!params}
        />
      ) : (
        <Driving
          vehicleId={vehicle.id}
          responseData={responseData}
          isCompressed={isCompressed}
          loadRide={handleDataAfterFinishedRide}
          isDetail={!!params}
          handleEndRideDateAndAddress={handleEndRideDateAndAddress}
        />
      );
    }
    return null;
  };

  React.useEffect(() => {
    if (vehicleData?.status && !origStatus) {
      setOrigStatus(vehicleData.status);
    }
  }, [origStatus, vehicleData?.status]);

  const loadPartData = async (dateFrom: Date, dateTo: Date): Promise<void> => {
    if (vehicle) {
      setLoader(true);
      const response = !rideId
        ? await getPartData('throttle', String(vehicle.id), [dateFrom, dateTo])
        : await getPartDataByRideId('throttle', String(vehicle.id), rideId, [dateFrom, dateTo]);
      if (response) {
        setResponseData(response);
        setLastClickedTimestamp(response[response.length - 1]?.timestamp);
      }
      setLoader(false);
    }
  };

  const resetPartData = () => {
    if (rideId) {
      getDataByRideIdFromLocalforage(rideId);
    } else {
      getDataFromLocalforage();
    }
  };

  const renderCompressedGraphAlert = () => {
    return getIsCompressed(responseData) ? (
      <div style={{ padding: params ? '20px 0 0 0' : '' }}>
        <CmdAlert
          id="velocity-graph-alert"
          data={{
            title: f({ id: 'graph.velocity.alert.title' }),
            message: params
              ? f({ id: 'graph.velocity.alert.message' })
              : f({ id: 'graph.onlinepanel.alert.message' }),
            type: 'info',
          }}
          noCancel
        />
      </div>
    ) : null;
  };

  return (
    <div
      className="container-fluid new-css-wrapper"
      data-cy="throttle-graph"
      style={{
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
      }}
    >
      {showSelectRidesModal && vehicle && (
        <SelectRidesModal
          vehicleId={String(vehicle.id)}
          closeCallback={handleModal}
          handleRide={handleRide}
          defaultRideId={rideId}
          defaultDates={defaultDates}
        />
      )}
      {params && vehicle ? (
        [
          <div className="row section page-head" data-cy="reports-head">
            <h4 data-cy="reports-title">
              {f({ id: 'enum.onlineMap.widget.types.throttle' })}{' '}
              <span className="text-gray-light">{getVehicleName(vehicle)}</span>
            </h4>
          </div>,
          <CmdBox
            id="fuellevelperc-basic-information"
            title={f({ id: 'graph.throttle.section.overview' }, { valueType: '%' })}
          >
            <RideStartAndEnd
              endDateAndAddress={endRideDateAndAddress}
              rideId={rideId}
              origStatus={origStatus}
              handleModal={handleModal}
              startDateAndAddress={startDateAndAddress}
              lastDateAndAddress={lastDateAndAddress}
            />
            {isOrigCompressed && (origStatus === 'STOP' || rideId) && (
              <PartSelecting
                dateFrom={getDateFrom(responseData)}
                dateTo={getDateTo(responseData)}
                loadPartData={loadPartData}
                loadData={resetPartData}
                data={responseData}
                rideId={rideId}
              />
            )}
            {renderCompressedGraphAlert()}
            <div style={{ marginTop: '10px', padding: '20px 0px 20px 25px' }}>{renderGraph()}</div>
          </CmdBox>,
          <PositionDetails vehicleId={vehicle.id} positions={responseData} />,
        ]
      ) : (
        <div>
          {renderCompressedGraphAlert()}
          {renderGraph()}
        </div>
      )}
    </div>
  );
}
